import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';

import { BehaviorSubject, combineLatest, merge, NEVER, Observable, ReplaySubject, Subject } from 'rxjs';
import {
    debounceTime,
    delay,
    distinctUntilChanged,
    map,
    mapTo,
    skip,
    startWith,
    switchMap,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ShFieldSearchComponent } from '../../../../advanced-search/filters/containers/field-search/field-search.component';
import { ShSearchBarModule } from '../../../../advanced-search/filters/search-bar.module';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { ActionGroup } from '../../../../elements/components/action-group/entities/action-group.entity';
import { ActionItem } from '../../../../elements/components/action-item/entities/action-item.entity';
import { MultiFormSelectOptions } from '../../../../form-controls/entities/multi-form-select-options.entity';
import { LoadingBehaviorSubject } from '../../../../helpers/loading-behavior-subject.helper';
import { isNotNullOrUndefined } from '../../../../helpers/typed-reactive-filters.helper';
import { ShGenericGridComponent } from '../../../grid-view/containers/generic-grid/generic-grid.component';
import { GridType } from '../../../grid-view/entities/grid-type.entity';
import { CellType } from '../../../list-view/components/cells/_base/table-cell.entity';
import { ShGenericListComponent } from '../../../list-view/containers/generic-list/generic-list.component';
import { ColumnDef } from '../../../list-view/entities/column-definition.entity';
import { SearchBarMode } from '../../../list-view/entities/filter-bar-mode.entity';
import { ShGenericMapComponent } from '../../../map-view/containers/generic-map/generic-map.component';
import { ActionMode, SelectionState } from '../../components/context-action-bar/entities/select-mode.entity';
import { ShDisplaySettingsBarComponent } from '../../components/display-settings-bar/display-settings-bar.component';
import {
    ALL_DISPLAY_MODES,
    DisplayMode,
    RESULTS_PER_PAGE_OPTIONS
} from '../../components/display-settings-bar/entities/display-mode.entity';
import { defaultSortInfo, SortInfo } from '../../components/display-settings-bar/entities/sort-info.entity';
import { ShPaginationComponent } from '../../components/pagination/pagination.component';
import { PaginatedResults, PaginationInfo, ResponsePaginationInfo } from '../../components/pagination/public_api';

import { ImageFitMode } from '../../../../config/entities/picture-fill-mode.entity';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { Comparable } from '../../../../helpers/entities/comparable.interface';
import { Trackable } from '../../../../helpers/entities/trackable.interface';
import { SortOption } from '../../components/display-settings-bar/entities/sort-option.entity';

@Component({
    standalone: true,
    selector: 'sh-generic-overview',
    templateUrl: './generic-overview.container.component.html',
    styleUrls: ['./generic-overview.container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ShDisplaySettingsBarComponent,
        ShFieldSearchComponent,
        ShGenericMapComponent,
        ShGenericListComponent,
        ShGenericGridComponent,
        ShPaginationComponent,
        ShSearchBarModule,
        TranslateModule
    ]
})
export class ShGenericOverviewContainerComponent<T extends Comparable & Trackable>
    extends BaseContainer
    implements OnInit, OnDestroy
{
    public readonly gridType: GridType = GridType.RECORD;
    public readonly selectedEntries$: ReplaySubject<T[]> = new ReplaySubject<T[]>();
    public readonly paginationInfo$: ReplaySubject<ResponsePaginationInfo> =
        new ReplaySubject<ResponsePaginationInfo>();

    public readonly entries$: ReplaySubject<T[]> = new ReplaySubject<T[]>();
    public readonly displayMode$: BehaviorSubject<DisplayMode> = new BehaviorSubject<DisplayMode>(DisplayMode.LIST);
    public readonly sortInfo$: BehaviorSubject<SortInfo> = new BehaviorSubject<SortInfo>(defaultSortInfo);
    public readonly sortOptions$: BehaviorSubject<SortInfo[]> = new BehaviorSubject<SortInfo[]>([defaultSortInfo]);
    public readonly isLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    public readonly searchBarModes: typeof SearchBarMode = SearchBarMode;
    public readonly displayModes: typeof DisplayMode = DisplayMode;

    public toggleActionGroup$!: Observable<ActionGroup>;
    public totalResults$: Observable<number>;

    private shiftPressed$: Observable<boolean>;
    private ctrlPressed$: Observable<boolean>;

    public usingBatch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly selectAll$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly deselectItem$: Subject<T> = new Subject<T>();
    private readonly selectItem$: Subject<T> = new Subject<T>();
    private readonly clickItem$: Subject<T> = new Subject<T>();
    private readonly keyPress$: BehaviorSubject<KeyboardEvent | null> = new BehaviorSubject<KeyboardEvent | null>(null);
    private readonly lastSelectedItem$: BehaviorSubject<T | null> = new BehaviorSubject<T | null>(null);

    @Input() columns: ColumnDef<any, T>[] = [];
    @Input() columnOptions: FormSelectOption[] = [];
    @Output() columnSelectionChanged = new EventEmitter<string[]>();

    // #region Filter bar
    @Input() searchBarMode: SearchBarMode = SearchBarMode.ADVANCED;
    @Input() searchBarActionItems?: ActionItem[];
    @Input() fieldSearchOptions: MultiFormSelectOptions;
    // #endregion

    // #region Display options
    @Input() showViewToggle = true;
    @Input() displayOptions: DisplayMode[] = ALL_DISPLAY_MODES;
    @Input() pictureFillMode: ImageFitMode = ImageFitMode.COVER;

    @Input() set displayMode(displayMode: DisplayMode) {
        this.displayMode$.next(displayMode);
    }

    @Output() readonly displayModeChanged: EventEmitter<DisplayMode> = new EventEmitter<DisplayMode>();
    // #endregion

    // #region Sorting options
    @Input() allowSort = true;

    @Input() set sortInfo(sortInfo: SortInfo) {
        this.sortInfo$.next(sortInfo);
    }

    @Input() set sortOptions(sortOptions: SortOption[]) {
        sortOptions = sortOptions ? sortOptions : [];
        this.sortOptions$.next(sortOptions);
    }

    @Output() readonly sortInfoChanged: EventEmitter<SortInfo> = new EventEmitter<SortInfo>();
    // #endregion

    // #region Selection
    // TODO: Rename this to something clearer
    @Input() availableSelect = true;
    @Input() disableSelectMessage = '';
    @Input() itemsSelectedLabelKey?: string;
    @Input() noItemsSelectedLabelKey?: string;

    @Input() set selectedItems(selectedItems: T[]) {
        if (!selectedItems) {
            return;
        }

        this.selectedEntries$.next(selectedItems);
    }

    // TODO: Used to enable batch select on reload of page. Rename this to something clearer
    @Input() set allSelected(allSelected: boolean) {
        this.usingBatch$.next(allSelected);
    }

    @Output() readonly itemsChecked: EventEmitter<T[]> = new EventEmitter<T[]>();
    // #endregion

    // #region Results and pagination
    @Input() resultsPerPageOptions: number[] = RESULTS_PER_PAGE_OPTIONS;
    @Input() scrollRef?: string;

    @Input() xxResultsLabelKey?: string;
    @Input() noResultsLabelKey?: string;

    @Input() set paginatedResults(paginatedResults: PaginatedResults<T> | null) {
        if (paginatedResults !== null) {
            this.entries$.next(paginatedResults.results);
            this.paginationInfo$.next(paginatedResults.paginationInfo);
            this.lastSelectedItem$.next(null);
        }
    }

    @Input() autoRefresh: boolean;
    @Input() set isLoading(loading: boolean) {
        this.isLoading$.next(loading);
    }

    @Output() readonly paginationInfoChanged: EventEmitter<PaginationInfo> = new EventEmitter<PaginationInfo>();
    // #endregion

    // #region Item actions
    @Input() actionItems?: ActionGroup[];
    @Input() contextActionGroup?: ActionGroup;
    @Input() canBatchSelect = false;

    @Output() readonly actionModeChanged: EventEmitter<ActionMode> = new EventEmitter<ActionMode>();
    // #endregion

    // #region Active items
    @Input() activeItem: T | null;
    @Input() highlightedItem: T | null;
    // #endregion

    // #region Error handling
    @Input() hasError = false;
    @Input() errorMessage: string;
    // #endregion

    // #region Click event propagation
    @Input() areItemsClickable: boolean;
    @Output() readonly itemClicked: EventEmitter<T> = new EventEmitter<T>();
    @Output() readonly thumbnailClicked: EventEmitter<T> = new EventEmitter<T>();
    // #endregion

    // #region Keyboard and mouse events
    @HostListener('window:keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
        this.keyPress$.next(e);
    }

    @HostListener('window:keyup', ['$event']) onKeyUp(e: KeyboardEvent) {
        this.keyPress$.next(null);
    }

    @HostListener('mousedown', ['$event']) onMousedown(e: MouseEvent) {
        // Prevent text-selection on SHIFT + click
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
            e.preventDefault();
        }
    }

    // Detect window blur (user switched tabs or browser hijacked keys)
    @HostListener('window:blur', ['$event'])
    handleBlur(event: FocusEvent) {
        this.keyPress$.next(null);
    }

    // Handle when page becomes hidden (e.g., user switches tabs)
    @HostListener('document:visibilitychange', [])
    onVisibilityChange() {
        if (document.hidden) {
            this.keyPress$.next(null);
        }
    }
    // #endregion

    constructor(private readonly host: ElementRef) {
        super();
    }

    public ngOnInit(): void {
        this.initKeyDownListeners();
        this.initSelectedEntriesListeners();

        this.initItemsCheckedOutput();
        this.initTotalResultsStream();

        this.initScrollToSelectedElement();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();

        this.displayMode$.complete();
        this.sortInfo$.complete();
        this.isLoading$.complete();
        this.selectAll$.complete();
        this.deselectItem$.complete();
        this.selectItem$.complete();
        this.clickItem$.complete();
        this.lastSelectedItem$.complete();
    }

    private initScrollToSelectedElement(): void {
        const clickScrollTrigger$: Observable<ScrollIntoViewOptions> = this.clickItem$.pipe(
            takeUntil(this.destroy$),
            delay(0),
            mapTo({ behavior: 'smooth' })
        );
        const displayModeScrollTrigger$: Observable<ScrollIntoViewOptions> = this.displayMode$.pipe(
            map(() => this.activeItem),
            isNotNullOrUndefined(),
            mapTo({}),
            delay(0)
        );

        const resizeEvents$: Observable<ResizeObserverEntry> =
            'ResizeObserver' in window ? this.initResizeEventsStream(this.host) : NEVER;

        const resizeScrollTrigger$: Observable<ScrollIntoViewOptions> = resizeEvents$.pipe(
            debounceTime(100),
            map(() => this.activeItem),
            isNotNullOrUndefined(),
            mapTo({})
        );

        merge(clickScrollTrigger$, displayModeScrollTrigger$, resizeScrollTrigger$)
            .pipe(delay(250), takeUntil(this.destroy$))
            .subscribe(scrollIntoViewOptions => this.ensureVisibilityOfActiveElement(scrollIntoViewOptions));
    }

    private ensureVisibilityOfActiveElement(scrollIntoViewOptions: ScrollIntoViewOptions = {}): void {
        const options: ScrollIntoViewOptions = {
            behavior: 'auto',
            block: 'nearest',
            inline: 'nearest',
            ...scrollIntoViewOptions
        };

        const elements: NodeListOf<HTMLElement> = (this.host.nativeElement as HTMLElement).querySelectorAll(
            '.sh-card-table__row--active, .sh-grid-item--active'
        );

        if (elements.length > 0) {
            elements[0].scrollIntoView(options);
        }
    }

    private initResizeEventsStream(element: ElementRef): Observable<ResizeObserverEntry> {
        const stream$: Observable<ResizeObserverEntry> = new Observable(subscriber => {
            // ResizeObserver will call callback _directly_ for (rendered) elements
            // See https://github.com/WICG/resize-observer/issues/8
            const resizeObserver: ResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                if (entries[0]) {
                    subscriber.next(entries[0]);
                }
            });

            resizeObserver.observe(element.nativeElement);
            return () => resizeObserver.unobserve(element.nativeElement);
        });
        return stream$;
    }

    public onPaginationInfoChanged(paginationInfo: PaginationInfo): void {
        this.paginationInfoChanged.emit(paginationInfo);
    }

    // #region Item Selection
    private initKeyDownListeners(): void {
        this.shiftPressed$ = this.keyPress$.pipe(
            map(keyEvent => (keyEvent ? keyEvent.shiftKey : false)),
            distinctUntilChanged(),
            startWith(false)
        );

        this.ctrlPressed$ = this.keyPress$.pipe(
            map(keyEvent => (keyEvent ? keyEvent.ctrlKey || keyEvent.metaKey : false)),
            distinctUntilChanged(),
            startWith(false)
        );
    }

    private initSelectedEntriesListeners(): void {
        if (!this.availableSelect) {
            this.selectedEntries$.next([]);
        }

        combineLatest([this.selectAll$, this.usingBatch$])
            .pipe(withLatestFrom(this.selectedEntries$, this.entries$), takeUntil(this.destroy$))
            .subscribe(values => {
                const [[selectAll, useBatch], selectedEntries, entries] = values;

                if (selectAll) {
                    this.addSelectedItems(selectedEntries, entries);
                    return;
                }

                if (!selectAll && !useBatch) {
                    this.clearSelectedItems(selectedEntries);
                    return;
                }
            });

        this.selectItem$
            .pipe(
                withLatestFrom(this.entries$, this.selectedEntries$, this.shiftPressed$, this.ctrlPressed$),
                takeUntil(this.destroy$)
            )
            .subscribe(([item, entries, selectedEntries, shiftPressed, ctrlPressed]) =>
                this.handleCheckboxSelection(entries, selectedEntries, item, shiftPressed, ctrlPressed, true)
            );

        this.deselectItem$
            .pipe(
                withLatestFrom(this.entries$, this.selectedEntries$, this.shiftPressed$, this.ctrlPressed$),
                takeUntil(this.destroy$)
            )
            .subscribe(([item, entries, selectedEntries, shiftPressed, ctrlPressed]) =>
                this.handleCheckboxSelection(entries, selectedEntries, item, shiftPressed, ctrlPressed, false)
            );

        this.clickItem$
            .pipe(
                withLatestFrom(this.entries$, this.selectedEntries$, this.shiftPressed$, this.ctrlPressed$),
                takeUntil(this.destroy$)
            )
            .subscribe(([item, entries, selectedEntries, shiftPressed, ctrlPressed]) => {
                if (shiftPressed || ctrlPressed) {
                    this.handleItemSelection(entries, selectedEntries, item, shiftPressed, ctrlPressed);
                    return;
                }

                if (this.areItemsClickable) {
                    this.itemClicked.emit(item);
                }
            });
    }

    private clearSelectedItems(selectedEntries: T[]): void {
        this.lastSelectedItem$.next(null);
        if (selectedEntries.length > 0) {
            this.selectedEntries$.next([]);
        }
    }

    private toggleSelectedItem(selectedEntries: T[], item: T): void {
        if (selectedEntries.findIndex(entry => entry.equals(item)) >= 0) {
            this.removeSelectedItem(selectedEntries, item);
        } else {
            this.addSelectedItem(selectedEntries, item);
        }
    }

    private addSelectedItem(selectedEntries: T[], item: T): void {
        this.selectedEntries$.next([...selectedEntries, item]);
    }

    private removeSelectedItem(selectedEntries: T[], item: T): void {
        this.selectedEntries$.next(selectedEntries.filter(entry => !entry.equals(item)));
    }

    private handleCheckboxSelection(
        entries: T[],
        selectedEntries: T[],
        item: T,
        shiftPressed: boolean,
        ctrlPressed: boolean,
        select: boolean
    ): void {
        if (shiftPressed || ctrlPressed) {
            this.handleItemSelection(entries, selectedEntries, item, shiftPressed, ctrlPressed);
            return;
        }

        if (select) {
            this.addSelectedItem(selectedEntries, item);
        } else {
            this.removeSelectedItem(selectedEntries, item);
        }
        this.lastSelectedItem$.next(item);
    }

    private handleItemSelection(
        entries: T[],
        selectedEntries: T[],
        item: T,
        shiftPressed: boolean,
        ctrlPressed: boolean
    ): void {
        const lastSelectedItem: T | null = this.lastSelectedItem$.value;

        if (shiftPressed && !!lastSelectedItem) {
            this.bulkToggleSelectedItems(entries, selectedEntries, lastSelectedItem, item);
        } else if (ctrlPressed || (shiftPressed && !lastSelectedItem)) {
            this.toggleSelectedItem(selectedEntries, item);
        }
        this.lastSelectedItem$.next(item);
    }

    private bulkToggleSelectedItems(entries: T[], selectedEntries: T[], lastSelectedItem: T, newSelectedItem: T): void {
        const select = !selectedEntries.includes(newSelectedItem);

        const newSelectedItemIndex: number = entries.findIndex(entry => entry.equals(newSelectedItem));
        const lastSelectedItemIndex: number = entries.findIndex(entry => entry.equals(lastSelectedItem));

        const itemsInRange: T[] =
            newSelectedItemIndex < lastSelectedItemIndex
                ? entries.slice(newSelectedItemIndex, lastSelectedItemIndex + 1)
                : entries.slice(lastSelectedItemIndex, newSelectedItemIndex + 1);

        if (select) {
            this.addSelectedItems(selectedEntries, itemsInRange);
        } else {
            this.removeSelectedItems(selectedEntries, itemsInRange);
        }
    }

    private addSelectedItems(selectedEntries: T[], items: T[]): void {
        const selectedEntriesIds = selectedEntries.map(entry => entry.trackBy());

        const toAdd: T[] = items.filter(item => !selectedEntriesIds.includes(item.trackBy()));

        this.selectedEntries$.next([...selectedEntries, ...toAdd]);
    }

    private removeSelectedItems(selectedEntries: T[], items: T[]): void {
        const itemsIds = items.map(entry => entry.trackBy());

        const filteredItems: T[] = selectedEntries.filter(selected => !itemsIds.includes(selected.trackBy()));
        this.selectedEntries$.next(filteredItems);
    }

    public handleSelectionChanged({ selectAll, deselectAll, actionMode }: SelectionState): void {
        this.usingBatch$.next(actionMode === ActionMode.BATCH);

        if (selectAll) {
            this.selectAll$.next(true);
            this.actionModeChanged.emit(actionMode);
            return;
        }

        // Always reset the action mode when we deselect all items
        if (deselectAll) {
            this.selectAll$.next(false);
            this.usingBatch$.next(false);
            this.actionModeChanged.emit(ActionMode.BULK);
            return;
        }
    }

    public handleSelect(item: T): void {
        this.selectItem$.next(item);
    }

    public handleDeselect(item: T): void {
        this.deselectItem$.next(item);
    }

    public onItemClicked(item: T): void {
        this.clickItem$.next(item);
    }

    public onCellClicked(event: { cellType: CellType; item: T }): void {
        if (event.cellType === CellType.MEDIA) {
            this.onThumbnailClicked(event.item);
        }
    }

    public onThumbnailClicked(item: T): void {
        this.thumbnailClicked.next(item);
    }

    // #endregion

    // #region Emitting events
    public onColumnSelectionChanged(selectedColumns: string[]): void {
        this.columnSelectionChanged.emit(selectedColumns);
    }

    public onViewModeSelected(displayMode: DisplayMode): void {
        this.displayModeChanged.emit(displayMode);
        this.displayMode$.next(displayMode);
    }

    public onSortInfoChanged(sortInfo: SortInfo): void {
        this.sortInfoChanged.emit(sortInfo);
        this.sortInfo$.next(sortInfo);
    }

    private initItemsCheckedOutput(): void {
        this.selectedEntries$.pipe(skip(1), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(entries => {
            this.itemsChecked.emit(entries);
        });
    }
    // #endregion

    private initTotalResultsStream(): void {
        this.totalResults$ = this.paginationInfo$.pipe(map(paginationInfo => paginationInfo.totalNumberOfResults));
    }

    public get selectedItemsCount(): Observable<number> {
        const filteredResults$: Observable<number> = this.paginationInfo$.pipe(map(info => info.totalNumberOfResults));
        const selectedResults$: Observable<number> = this.selectedEntries$.pipe(
            map(selectedEntries => selectedEntries.length)
        );
        return this.usingBatch$.pipe(switchMap(useBatch => (useBatch ? filteredResults$ : selectedResults$)));
    }

    public get isIndeterminate(): Observable<boolean> {
        return combineLatest([this.entries$, this.selectedItemsCount]).pipe(
            map(([entries, count]) => count !== entries.length)
        );
    }
}
